/**
 * Льготная цена подписки в первые сутки: полоса с обратным отсчётом и окно
 * при первом открытии.
 *
 * Срок приходит с сервера (`profile.introUntil`) и считается там от времени
 * регистрации. Своей арифметики здесь нет намеренно: на двух устройствах она
 * дала бы два разных «осталось», а решает всё равно сервер — в момент, когда
 * выставляет счёт.
 *
 * **Тикаем раз в секунду только пока полоса на экране.** Таймер, оставленный
 * висеть после того, как окно закрылось, — это перерисовка всего дерева раз в
 * секунду до конца сеанса.
 */
import { useEffect, useState } from 'react'
import { useSirr } from '../store'

/** Окно показываем один раз за установку: второе такое же — уже назойливость. */
const SEEN_KEY = 'sirr_intro_seen'

/** Часы:минуты:секунды до конца срока; пусто, если он уже вышел. */
function left(until: number, now: number): string {
  const ms = until - now
  if (ms <= 0) return ''
  const total = Math.ceil(ms / 1000)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

/** Тик раз в секунду, пока срок не вышел. */
function useCountdown(until: number): string {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (until <= Date.now()) return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [until])
  return left(until, now)
}

/**
 * Полоса под шапкой на всех экранах. Нажатие ведёт на подписку — она же и
 * есть предложение, о котором полоса напоминает.
 */
export function IntroStrip({ onOpen }: { onOpen: () => void }) {
  const { profile, t } = useSirr()
  const until = profile.introUntil ?? 0
  const time = useCountdown(until)
  if (!time) return null

  return (
    <button
      type="button"
      onClick={onOpen}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        width: '100%',
        border: 'none',
        borderBottom: '1px solid rgba(200,162,78,.35)',
        padding: '7px 14px',
        background: 'linear-gradient(100deg, rgba(120,74,28,.75), rgba(48,26,60,.75))',
        color: 'var(--gold-bright, #e8c877)',
        fontFamily: 'var(--sans)',
        fontSize: 12.5,
        cursor: 'pointer',
        flexShrink: 0,
      }}
    >
      <span>✦ {t('introStrip')}</span>
      {/* Моноширинным, иначе строка дёргается каждую секунду: цифры разной
          ширины сдвигают весь текст рядом. */}
      <span style={{ fontFamily: 'var(--mono)', letterSpacing: 0.5 }}>{time}</span>
    </button>
  )
}

/**
 * Окно при первом открытии. Показывается один раз: предложение живёт сутки, и
 * человек, закрывший его, не должен видеть то же самое при каждом входе —
 * дальше о нём напоминает полоса.
 */
export function IntroModal({ onOpen }: { onOpen: () => void }) {
  const { profile, t } = useSirr()
  const until = profile.introUntil ?? 0
  const [closed, setClosed] = useState(() => localStorage.getItem(SEEN_KEY) === '1')
  const time = useCountdown(until)

  // Метку ставим при показе, а не при закрытии: человек может закрыть окно
  // жестом «назад» или уйти из приложения, и тогда оно всплыло бы снова.
  useEffect(() => {
    if (time && !closed) localStorage.setItem(SEEN_KEY, '1')
  }, [time, closed])

  if (!time || closed) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 90,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'rgba(9,5,18,.82)',
      }}
      onClick={() => setClosed(true)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 340,
          border: '1px solid rgba(200,162,78,.5)',
          borderRadius: 20,
          padding: '22px 20px',
          textAlign: 'center',
          background: 'linear-gradient(150deg, rgba(96,58,24,.7), rgba(18,10,31,.95))',
          boxShadow: 'var(--gold-glow)',
        }}
      >
        <div style={{ fontSize: 30, color: 'var(--gold)', marginBottom: 8 }}>✦</div>
        <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 23, lineHeight: 1.25 }}>
          {t('introTitle')}
        </div>
        <p style={{ fontSize: 13, color: 'var(--ink-muted)', margin: '10px 0 0', lineHeight: 1.5 }}>
          {t('introBody')}
        </p>
        {/* Пробный период — отдельной строкой и другим цветом: это второе
            предложение, а не уточнение к первому. Слить их в один абзац
            значило бы получить «подписка дешевле, и три дня бесплатно» —
            читается как отложенное списание, которого у нас нет. */}
        <p style={{ fontSize: 12.5, color: 'var(--gold)', margin: '8px 0 0', lineHeight: 1.45 }}>
          {t('introTrial')}
        </p>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 26, color: 'var(--gold)', margin: '14px 0 2px' }}>
          {time}
        </div>
        <div style={{ fontSize: 11, color: 'var(--ink-faint)' }}>{t('introLeft')}</div>

        <button
          onClick={() => {
            setClosed(true)
            onOpen()
          }}
          style={{
            width: '100%',
            marginTop: 16,
            background: 'var(--gold-gradient)',
            color: '#1a1020',
            border: 'none',
            borderRadius: 12,
            padding: '13px 14px',
            fontFamily: 'var(--sans)',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          {t('introGo')}
        </button>
        <button
          onClick={() => setClosed(true)}
          style={{
            marginTop: 10,
            background: 'none',
            border: 'none',
            color: 'var(--ink-faint)',
            fontFamily: 'var(--sans)',
            fontSize: 12.5,
            cursor: 'pointer',
          }}
        >
          {t('introLater')}
        </button>
      </div>
    </div>
  )
}
